/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import SimpleModal from '@bufferapp/ui/SimpleModal';
import Image from '@bufferapp/components/Image';
import { BufferTracker, Product } from '@bufferapp/buffer-tracking-browser-ts';
import { useTranslation } from 'react-i18next';
import { useAccount, useOrganizationLimits } from '~/accountContext';
import { SEGMENT_NAMES } from '~/constants';

import {
  ModalWrapperStyled,
  ModalActionsStyled,
  HeaderStyled,
  CenteredText,
  HeroImage,
  ListStyled,
  ListItemStyled,
  CheckmarkIconStyled,
  ButtonStyled,
  FooterStyled,
} from './styles';

export const IdeasLimitReachedModal = ({ onClose, type }): JSX.Element => {
  const { t } = useTranslation();
  const accountContext = useAccount();
  const account = accountContext?.account;
  const organizationId = account?.currentOrganization?.id;
  const { ideas: ideasLimit } = useOrganizationLimits();

  useEffect(() => {
    if (type !== 'paid') {
      const eventData = {
        product: Product.Publish,
        upgradePathName: `ideas-upgrade-path-modal-free-to-${type}`,
        organizationId: organizationId || '',
      };

      BufferTracker.upgradePathViewed(eventData);
    }
  }, []);

  useEffect(() => {
    if (type === 'trial') {
      const { COMPONENTS, actions } = window?.appshell || {};
      actions.renderComponent({
        containerId: 'ideasReachLimitCta',
        componentKey: COMPONENTS.startTrialButton,
        options: {
          cta: SEGMENT_NAMES.IDEAS_LIMIT_TRIAL,
          upgradePathName: 'ideasLimit-trial',
        },
      });
    }
  }, []);

  const paidContent = () => {
    return (
      <>
        <HeaderStyled type="h2">
          {t('content.modals.ideasLimitReached.paid.title', {
            limit: ideasLimit,
          })}
        </HeaderStyled>
        <CenteredText type="p">
          {t('content.modals.ideasLimitReached.paid.description')}
        </CenteredText>
      </>
    );
  };

  const modalActionButtons = (modalType: string) => {
    return (
      <ModalActionsStyled>
        {modalType === 'trial' && <div id="ideasReachLimitCta" />}
        {modalType === 'upgrade' && (
          <ButtonStyled
            type="primary"
            label={t('content.modals.ideasLimitReached.free.upgrade.button')}
            onClick={() => {
              onClose(false);
              const { MODALS, actions } = window?.appshell || {};
              actions.openModal(MODALS.planSelector, {
                cta: SEGMENT_NAMES.IDEAS_LIMIT_UPGRADE,
                upgradePathName: 'ideasLimit-upgrade',
              });
            }}
          />
        )}
      </ModalActionsStyled>
    );
  };

  const freeContent = (modalType: string) => {
    return (
      <>
        <HeaderStyled type="h2">
          {t(`content.modals.ideasLimitReached.free.title`, {
            limit: ideasLimit,
          })}
        </HeaderStyled>
        <CenteredText type="p">
          {t(`content.modals.ideasLimitReached.free.description`)}
        </CenteredText>
        <ListStyled>
          <ListItemStyled>
            <CheckmarkIconStyled />
            {t(`content.modals.ideasLimitReached.free.${modalType}.listItem1`)}
          </ListItemStyled>
          <ListItemStyled>
            <CheckmarkIconStyled />
            {t(`content.modals.ideasLimitReached.free.${modalType}.listItem2`)}
          </ListItemStyled>
          <ListItemStyled>
            <CheckmarkIconStyled />
            {t(`content.modals.ideasLimitReached.free.${modalType}.listItem3`)}
          </ListItemStyled>
        </ListStyled>
        {modalActionButtons(modalType)}
        {modalType === 'trial' && (
          <FooterStyled type="p">
            {t('content.modals.ideasLimitReached.free.trial.footer')}
          </FooterStyled>
        )}
      </>
    );
  };

  const getModalContent = (modalType: string) => {
    switch (modalType) {
      case 'paid':
        return paidContent();
      case 'trial':
      case 'upgrade':
        return freeContent(modalType);
      default:
        return paidContent();
    }
  };

  return (
    <SimpleModal closeAction={() => onClose(false)}>
      <ModalWrapperStyled>
        <HeroImage>
          <Image
            src="https://buffer-publish.s3.amazonaws.com/images/consumption-limit-banner2x.jpg"
            alt="Consumption limits reached"
            width="198px"
          />
        </HeroImage>
        {getModalContent(type)}
      </ModalWrapperStyled>
    </SimpleModal>
  );
};
